import React from 'react';
import { Shield, FileText, Lock, Bell, Calendar, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO 
        title="Privacy Policy & Terms - MSK and Sports Physio Harrow"
        description="Privacy policy and terms for MSK and Sports Physio. GDPR compliant, strict 48-hour cancellation policy. Learn how we protect your personal and health information."
        keywords="physiotherapy privacy policy, GDPR physiotherapy, cancellation policy physio Harrow"
        type="website"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge className="bg-[#dbeafe] text-[#2563eb] hover:bg-[#dbeafe] mb-4">Privacy & Policies</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy & Terms
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Your privacy and trust are important to us. Read how we protect your information and our practice policies.
          </p>
          <p className="text-sm text-gray-500 mt-4">Last updated: December 2025</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4 lg:px-8">
        <div className="container mx-auto max-w-4xl space-y-8">
          
          {/* Data Protection & Privacy */}
          <Card className="border-2 border-[#dbeafe]">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-[#dbeafe] rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#2563eb]" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Data Protection & Privacy</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Information We Collect</h3>
                <p className="mb-3">
                  MSK and Sports Physio collects and processes personal information necessary for providing physiotherapy services. This includes:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span><strong>Personal Details:</strong> Name, date of birth, address, contact information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span><strong>Medical Information:</strong> Health history, injury details, treatment notes, assessment findings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span><strong>Insurance Details:</strong> Provider information and policy numbers (if applicable)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span><strong>Communication Records:</strong> Emails, messages, and appointment correspondence</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How We Use Your Information</h3>
                <p className="mb-3">Your personal data is used solely for:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Providing physiotherapy treatment and care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Managing appointments and clinical records</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Processing insurance claims and payments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Communication regarding your treatment and appointments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Compliance with legal and professional obligations</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Data Security</h3>
                <p>
                  We implement appropriate technical and organisational measures to protect your personal information against 
                  unauthorised access, alteration, disclosure, or destruction. All records are stored securely in compliance 
                  with UK GDPR and Data Protection Act 2018 requirements.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Consent */}
          <Card className="border-2 border-[#dbeafe]">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-[#dbeafe] rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#2563eb]" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Consent for Treatment</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Informed Consent</h3>
                <p className="mb-3">
                  Before commencing treatment, you will be asked to provide informed consent. This means:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>We will explain your diagnosis and proposed treatment plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>You will have the opportunity to ask questions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>Potential risks and benefits will be discussed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2563eb] mt-1">•</span>
                    <span>You may withdraw consent at any time</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Parental Consent</h3>
                <p>
                  For patients under 16 years of age, parental or guardian consent is required for assessment and treatment. 
                  Parents/guardians must be present during paediatric assessments and treatment sessions.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Photography & Video Consent</h3>
                <p>
                  Photographs or videos may be taken for clinical documentation purposes only, with your explicit consent. 
                  These will never be shared publicly or used for marketing without separate written permission.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cancellation Policy */}
          <Card className="border-2 border-orange-100 bg-orange-50/30">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Cancellation & Missed Appointment Policy</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <p className="font-medium text-gray-900">
                We understand that unexpected circumstances arise. However, to respect everyone's time and ensure 
                appointment availability for all patients, we have the following policy:
              </p>

              <div className="bg-white rounded-lg p-6 border-2 border-orange-200">
                <h3 className="font-bold text-orange-600 mb-4 text-lg">48-Hour Cancellation Notice Required</h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Bell className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Advance Cancellation (48+ hours notice)</p>
                        <p className="text-sm mt-1">No charge. Free to reschedule or cancel.</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-start gap-3 mb-2">
                      <Bell className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Late Cancellation (Less than 48 hours notice)</p>
                        <p className="text-sm mt-1">100% of session fee will be charged.</p>
                        <p className="text-xs text-gray-600 mt-1">
                          This allows us to potentially offer the slot to another patient.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-start gap-3 mb-2">
                      <Bell className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Missed Appointment (No notice)</p>
                        <p className="text-sm mt-1">100% of session fee will be charged.</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Failure to attend without prior notification results in full charge.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How to Cancel or Reschedule</h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="mb-3">Please contact us as soon as possible if you need to change your appointment:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#2563eb]" />
                      <div>
                        <p className="text-sm font-medium">Email:</p>
                        <a href="mailto:mskandsportsphysio@gmail.com" className="text-[#2563eb] hover:underline text-sm">
                          mskandsportsphysio@gmail.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#2563eb]" />
                      <div>
                        <p className="text-sm font-medium">Phone:</p>
                        <a href="tel:07747075809" className="text-[#2563eb] hover:underline text-sm">
                          07747075809
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#eff6ff] rounded-lg p-4 border-l-4 border-[#2563eb]">
                <p className="text-sm">
                  <strong>Emergency or Illness:</strong> We understand genuine emergencies and acute illness happen. 
                  Please contact us to discuss - we handle these situations with compassion and flexibility.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Professional Standards */}
          <Card className="border-2 border-[#dbeafe]">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-[#dbeafe] rounded-lg flex items-center justify-center">
                  <Lock className="w-6 h-6 text-[#2563eb]" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Professional Standards</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <p>
                MSK and Sports Physio operates in accordance with the standards set by professional regulatory bodies including:
              </p>
              <ul className="space-y-2 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#2563eb] mt-1">•</span>
                  <span>Health and Care Professions Council (HCPC)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#2563eb] mt-1">•</span>
                  <span>Chartered Society of Physiotherapy (CSP)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#2563eb] mt-1">•</span>
                  <span>UK GDPR and Data Protection Act 2018</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Contact for Policy Questions */}
          <Card className="border-2 border-gray-200 bg-gray-50">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Questions About Our Policies?</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about our privacy policy, data protection practices, or any of our policies, 
                please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href="mailto:mskandsportsphysio@gmail.com"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2563eb] text-white rounded-lg hover:bg-[#1d4ed8] transition-colors duration-200"
                >
                  <Mail className="w-5 h-5" />
                  Email Us
                </a>
                <a 
                  href="tel:07747075809"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-blue-600 text-[#2563eb] rounded-lg hover:bg-[#eff6ff] transition-colors duration-200"
                >
                  <Mail className="w-5 h-5" />
                  Call Us
                </a>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
